import React from 'react';

/* -------------------------------------------------------
   Config
------------------------------------------------------- */
const COLORS = [
  '#fca5a5', '#fcd34d', '#86efac', '#93c5fd',
  '#d8b4fe', '#f9a8d4', '#fdba74', '#cbd5e1',
];
/** pick a stable pastel for any integer id */
const colorFor = id => COLORS[Math.abs(id) % COLORS.length];

/** "HH:MM:SS" -> 8.5  (for 08:30) */
const toFloat = (t='00:00:00') => {
  const [h,m] = t.split(':').map(Number);
  return h + m/60;
};

/** simple overlap test */
const overlap = (a1,a2,b1,b2) => a1 < b2 && a2 > b1;

/** pretty hour labels */
const hourLabel = h => {
  const ampm = h < 12 ? 'AM' : 'PM';
  let h12 = h % 12; if (h12 === 0) h12 = 12;
  return `${h12}:00 ${ampm}`;
};

/* -------------------------------------------------------
   MAIN COMPONENT
   - Hours are columns (across the top)
   - Technicians are rows   (left column frozen)
------------------------------------------------------- */
const DayViewTimelineWip = ({
  selectedDay,
  Jobs = [],
  technicians  = [],        // <-- added
  onEventClick,
  onEmptyClick,
}) => {
  /* ---------- constants for layout ---------- */
  const hourCols     = 24;          // 0-23
  const colWidth     = 90;          // px
  const rowHeight    = 70;          // px
  const timeColSpan  = hourCols * colWidth;   // width of time grid

  /* ---------- group Jobs by lead tech ---------- */
  const techMap = new Map();
  Jobs.forEach(i => {
    const leadId = i.tech_id ?? 'unknown';
    if (!techMap.has(leadId)) techMap.set(leadId, []);
    techMap.get(leadId).push(i);
  
    const addlId = i.additional_tech_id;
    if (addlId && addlId !== leadId) {
      if (!techMap.has(addlId)) techMap.set(addlId, []);
      techMap.get(addlId).push(i);
    }
  });
  

  /* ---------- helper: render blocks for ONE tech ---------- */
  const renderBlocks = (inspList, techId) => {
    return inspList.map((insp, idx) => {
      const start = toFloat(insp.start_time   || '00:00:00');
      const end   = toFloat(insp.end_time  || '23:59:59');
      const left  = start * colWidth;
      const width = Math.max((end-start)*colWidth, colWidth/2);

      /* find overlaps within SAME tech to stack side-by-side */
      const siblings = inspList.filter(o =>
        overlap(start, end, toFloat(o.start_time), toFloat(o.end_time))
      ).sort((a,b)=> toFloat(a.start_time)-toFloat(b.start_time));

      const index      = siblings.indexOf(insp);
      const sibCount   = siblings.length;
      const sibWidth   = width / sibCount;
      const sibLeft    = left + index*sibWidth;

      return (
        <div key={idx}
          onClick={e=>{ e.stopPropagation(); onEventClick(insp); }}
          style={{
            position:'absolute', top:4, bottom:4,
            left:sibLeft, width:sibWidth-4,
            background:colorFor(techId),
            borderRadius:4, padding:4, overflow:'hidden',
            fontSize:11, cursor:'pointer',
          }}
        >
        {/* ▸ ribbon if COMPLETED */}
        {String(insp.status) === 'Completed' && (
          <span
            style={{
              /* --- position --- */
              position: 'absolute',
              top:   62,
              right: 0,

              /* --- banner look --- */
              width: 110,                 // wide enough to cross the corner
              textAlign: 'center',
              background: '#16a34a',
              color: '#fff',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.5px',
              padding: '2px 0',

              /* --- diagonal effect --- */
              transform: 'rotate(45deg)',
              transformOrigin: 'top right',

              /* --- a little shadow so it pops --- */
              boxShadow: '0 1px 3px rgba(0,0,0,.35)',
              zIndex: 9,
            }}
          >
            COMPLETED
          </span>
        )}   
          <strong>{insp.job_site||'No site'}</strong><br/>
          <span>{insp.customer_name||'No cust'}</span><br/>
          <span>
            {insp.start_time?.slice(0,5)}-{insp.end_time?.slice(0,5)}
          </span>
        </div>
      );
    });
  };

  /* ---------- rows (one per tech) ---------- */
  const rows = technicians.map((tech, rIdx) => {
    const techId  = tech.id ?? 'unknown';
    const list    = techMap.get(techId) || [];

    return (
      <div key={techId} style={{ display:'grid',
        gridTemplateColumns:`200px ${timeColSpan}px`,
        borderBottom:'1px solid #e5e7eb', height:rowHeight,
        position:'relative' }}
        /* empty-click gives hour/minute + techId */
        onClick={e=>{
          const rect = e.currentTarget.getBoundingClientRect();
          const x    = e.clientX - rect.left - 200; // after frozen col
          if (x<0) return;            // clicked on name column
          const hourFloat = x / colWidth;
          const hour   = Math.floor(hourFloat);
          const minute = Math.round((hourFloat-hour)*60/15)*15; // snap 15 min
          onEmptyClick({ hour, minute, techId });
        }}
      >
        {/* 1) frozen tech name column */}
        {/* <div style={{ background:'#fafafa', padding:'4px 6px',
                      borderRight:'1px solid #e5e7eb',
                      whiteSpace:'nowrap', overflow:'hidden' }}> */}
          <div style={{ background:'#fafafa', padding:'4px 6px',
                        borderRight:'1px solid #e5e7eb',
                        whiteSpace:'nowrap', overflow:'hidden',
                        position:'sticky', left:0, zIndex:20 /* NEW */ }}>
          <span className="font-semibold">{tech.first_name} {tech.last_name}</span>
          <div className="text-xs text-gray-500">{tech.department}</div>
        </div>

        {/* 2) timeline grid cell */}
        <div style={{ position:'relative' }}>
          {/* hour vertical lines */}
          {Array.from({length:hourCols}).map((_,h)=>(
            <div key={h} style={{
              position:'absolute', top:0, bottom:0,
              left:h*colWidth, width:0,
              borderLeft:'1px solid #eee'
            }}/>
          ))}
          {/* inspection blocks */}
          {renderBlocks(list, techId)}
        </div>
      </div>
    );
  });

  /* ---------- hour header row ---------- */
  // const header = (
  //   <div style={{ display:'grid',
  //     gridTemplateColumns:`200px ${timeColSpan}px`,
  //     borderBottom:'2px solid #e5e7eb', fontWeight:600
  //   }}>
    const header = (
      <div
        /*  stick this whole row to the top  */
        style={{
          position:'sticky', top:0, zIndex:10,      /*  <-- NEW  */
          display:'grid',
          gridTemplateColumns:`200px ${timeColSpan}px`,
          borderBottom:'2px solid #e5e7eb',
          fontWeight:600,
          background:'#f9fafb'                      /*  gives solid bg while sticky  */
        }}
      >  
      {/* blank cell */}
      {/* <div style={{ background:'#f3f4f6' }}/> */}
      <div
         /* keep this cell sticky too so it lines up */
        style={{ background:'#f3f4f6', position:'sticky', left:0, zIndex:31 }} />      
      {/* hour labels */}
      <div style={{ display:'grid',
        gridTemplateColumns:`repeat(${hourCols}, ${colWidth}px)` }}>
        {Array.from({length:hourCols}).map((_,h)=>(
          <div key={h} style={{ textAlign:'center', borderLeft:'1px solid #e5e7eb',
                                background:'#f9fafb' }}>
            {hourLabel(h)}
          </div>
        ))}
      </div>
    </div>
  );

  /* ---------- final render ---------- */
  return (
    <div className="border rounded overflow-x-auto">
      {/* day title */}
      <div className="text-center py-2 font-bold">
        {selectedDay.toLocaleDateString('en-US', {
          weekday:'long', month:'short', day:'numeric'
        })}
      </div>

      {/* timeline
      {header}
      {rows} */}
      {/* timeline (scrollable) */}
      <div style={{
          maxHeight:'70vh',        // you can tweak this height
          overflowY:'auto',
          position:'relative'      // required for sticky children
        }}>
        {header}
        {rows}
      </div>      
    </div>
  );
};

export default DayViewTimelineWip;
